// ─── Public Status Page — entry point ────────────────────────────────
//
// This is NOT main.tsx, and the difference is the whole point of the feature.
// The status page has to render for an anonymous stranger while the API and the
// database are DOWN, so every piece of the authenticated app's bootstrap is
// deliberately ABSENT here:
//
//   • AuthGate            — main.tsx wraps the entire RouterProvider in it, so
//                           nothing renders until a session resolves. On a
//                           public page that is an infinite spinner for a
//                           visitor who has no account and never will.
//   • installAuthFetch()  — wraps window.fetch so any /api 401 raises the lock
//                           screen. A stranger's first request would 401 and
//                           they would be shown a login prompt instead of the
//                           outage they came to read about.
//   • the service worker  — the PWA precaches ~41 MB of admin bundle at root
//                           scope. Registering it here would download the whole
//                           signed-in app onto a customer's phone.
//   • the PWA manifest    — "Install PCP?" is a nonsensical prompt for someone
//                           checking whether their invoicing system is up.
//                           (vite.config.ts strips both from status.html.)
//   • error-reporter      — POSTs to /api/app-errors, which is behind auth and
//                           is usually the thing that is already broken.
//   • initObservability   — OpenObserve RUM + session replay on a public page is
//                           a privacy problem we have no consent for.
//   • react-query         — one endpoint, one poll loop (lib/useStatusData.ts).
//   • react-router        — two routes (status/router.tsx, ~50 lines).
//   • zustand             — no cross-tree state to share.
//
// What remains is React, the error boundary, and a matchMedia listener.

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { StatusApp } from './status/App';
import { StatusErrorBoundary } from './status/components/StatusErrorBoundary';
import './status/status.css';

// The pre-paint stamp in public/status-theme.js has already set data-theme and
// data-mode before this bundle was even requested (so there is no flash of the
// wrong theme). All we do here is KEEP data-mode in sync if the visitor flips
// their OS between light and dark while the tab is open. There is no theme
// switcher and no localStorage on the public page: the reader's system
// preference is the only input.
function trackColorScheme(): void {
  try {
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = (dark: boolean) => {
      document.documentElement.setAttribute('data-mode', dark ? 'dark' : 'light');
    };
    apply(query.matches);
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', (event) => apply(event.matches));
    } else if (typeof (query as MediaQueryList & { addListener?: (cb: (e: MediaQueryListEvent) => void) => void }).addListener === 'function') {
      // Safari < 14 — the deprecated API is the only one available there.
      (query as MediaQueryList & { addListener: (cb: (e: MediaQueryListEvent) => void) => void }).addListener((event) =>
        apply(event.matches),
      );
    }
  } catch {
    /* a browser without matchMedia keeps whatever status-theme.js stamped */
  }
}

trackColorScheme();

const rootElement = document.getElementById('status-root');

if (!rootElement) {
  throw new Error('Status root element not found');
}

createRoot(rootElement).render(
  <StrictMode>
    <StatusErrorBoundary>
      <StatusApp />
    </StatusErrorBoundary>
  </StrictMode>,
);
